import { ChangeDetectionStrategy, Component, OnInit, ViewChild } from '@angular/core';
import { LeadSellerGroupService } from '../../services/lead-seller-group/lead-seller-group.service';
import { Observable, Subject } from 'rxjs';
import { take, takeUntil } from 'rxjs/operators';
import {
  selectDisplayText,
  selectedGroupMemberUuids,
  selectSelectedDealersCount,
  selectSingleDealerGroupName
} from '../../services/lead-seller-group/reducer';
import { DealerGroupsState } from '../../interfaces/dealer-group.interface';
import { Store } from '@ngxs/store';
import { DoSearch, UpdateLeadSellerPayload } from 'remarketing-angular-library';
import { TranslocoDirective, TranslocoService } from '@jsverse/transloco';
import { PopoverModule } from 'ngx-bootstrap/popover';
import {
  DealerGroupPopoverComponent
} from '../dealer-group-popover/dealer-group-popover.component';
import { CommonModule } from '@angular/common';

enum ButtonDisplayText {
  ALL_GROUPS = 'ALL_GROUPS',
  NO_GROUPS = 'NO_GROUPS',
  MULTI_GROUPS = 'MULTI_GROUPS'
}

@Component({
    selector: 'app-dealer-group',
    templateUrl: './dealer-group.component.html',
    styleUrls: ['./dealer-group.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [TranslocoDirective, PopoverModule, DealerGroupPopoverComponent, CommonModule]
})
export class DealerGroupComponent implements OnInit {
  private readonly destroyed$ = new Subject();
  @ViewChild('popover') popover;
  displayText: string;

  readonly buttonText$: Observable<string> = this.leadSellerGroupService.select(selectDisplayText);
  readonly totalSelected$: Observable<number> = this.leadSellerGroupService.select(selectSelectedDealersCount);
  readonly singleLeadSellerGroup$: Observable<string> = this.leadSellerGroupService.select(selectSingleDealerGroupName);
  singleLeadSellerGroupName: string;

  preState: DealerGroupsState;
  dealerGroupTranslations = {
    allGroups: '',
    noGroups: '',
    multiGroups: ''
  };

  constructor(
    readonly leadSellerGroupService: LeadSellerGroupService,
    private readonly store: Store,
    private readonly translocoService: TranslocoService
  ) {}

  ngOnInit(): void {
    this.singleLeadSellerGroup$.subscribe(res => {
      this.singleLeadSellerGroupName = res;
    });

    this.buttonText$.subscribe(response => {
      this.getTranslations(response);
    });

    if (this.singleLeadSellerGroupName) {
      this.displayText = this.singleLeadSellerGroupName;
    }
  }

  getButtonDisplayTextTranslation(response) {
    switch (response) {
      case ButtonDisplayText.ALL_GROUPS:
        this.displayText = this.dealerGroupTranslations.allGroups;
        break;
      case ButtonDisplayText.NO_GROUPS:
        this.displayText = this.dealerGroupTranslations.noGroups;
        break;
      case ButtonDisplayText.MULTI_GROUPS:
        this.displayText = this.dealerGroupTranslations.multiGroups;
        break;
    }
  }

  getTranslations(response) {
    this.translocoService
      .selectTranslateObject('UIComponents.dealerGroupTranslations')
      .pipe(takeUntil(this.destroyed$))
      .subscribe(translate => {
        this.dealerGroupTranslations.allGroups = translate.allGroups;
        this.dealerGroupTranslations.noGroups = translate.noGroups;
        this.dealerGroupTranslations.multiGroups = translate.multiGroups;
        this.getButtonDisplayTextTranslation(response);
      });
  }

  leadSellerSearch() {
    this.preState = null;

    this.popover.hide();
    const currentState = this.leadSellerGroupService.snapShot();

    this.store.dispatch([new UpdateLeadSellerPayload(selectedGroupMemberUuids(currentState)), new DoSearch()]);

    this.leadSellerGroupService.cacheState();
  }

  onShown() {
    this.leadSellerGroupService.state$.pipe(take(1)).subscribe(state => (this.preState = state));
  }

  onHidden() {
    if (this.preState !== null) {
      this.leadSellerGroupService.setState(this.preState);
    }
  }

  ngOnDestroy(): void {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }
}
